<template>
  <mdb-container class="gp-has-header">
    <div class="text-left">
      <div v-if="showMain">
        <h3>System Subscription Plans</h3>
        <mdb-btn size="sm" rounded @click="addPlan" icon="plus-circle">Add Plan</mdb-btn>
        <div v-if="plans.length>0">
          <mdb-tbl responsive striped>
            <mdb-tbl-head class="secondary-color">
              <tr>
                <th>Plan</th>
                <th>Frequency</th>
                <th>Price</th>
                <th>Member May Select</th>
                <th>Membership Plan</th>
                <th>Ownership Plan</th>
                <th></th>
              </tr>
            </mdb-tbl-head>
            <mdb-tbl-body>
              <tr :key="i" v-for="(plan,i) in plans">
                <td>{{ plan.planName }}</td>
                <td>{{ plan.term }}</td>
                <td class="text-right">{{ plan.priceFormatted }} ({{ settings.defaultCurrency }})</td>
                <td class="text-center">
                  <mdb-icon icon="check" v-if="plan.chooseOnJoin"/>
                </td>
                <td class="text-center">
                  <mdb-icon icon="check" v-if="plan.membershipPlan"/>
                </td>
                <td class="text-center">
                  <mdb-icon icon="check" v-if="plan.ownershipPlan"/>
                </td>

                <td>
                  <mdb-btn @click="editPlan(plan)" floating icon="pencil" size="sm" tag="a"/>
                  <mdb-btn @click="clonePlan(plan)" floating icon="copy" size="sm" tag="a"/>
                  <mdb-btn @click="deletePlan(plan)" floating icon="trash" color="danger" v-if="plan.subscriptions == 0" size="sm" tag="a"/>
                </td>
              </tr>
            </mdb-tbl-body>
          </mdb-tbl>
        </div>
        <div v-if="plans.length==0">
          No plans set up.
        </div>
      </div>

      <div v-if="showEdit">
        <mdb-card class="mb-4">
          <mdb-card-header>
            <h3>Edit Plan</h3>
          </mdb-card-header>
          <mdb-card-body>
            <mdb-btn size="sm" rounded icon="arrow-left" @click="cancelEditing">Back to List</mdb-btn>
            <mdb-input label="Plan Name" placeholder="Name" required type="text" v-model="editing.planName"/>
            <div class="md-form">
              <label class="active">Term</label>
              <select class="browser-default gp-custom-select" id="term" v-model="editing.term">
                <option>Yearly</option>
                <option>Monthly</option>
              </select>
            </div>
            <mdb-input :step="0.01" :label="'Price (' + settings.defaultCurrency + ')'" placeholder="Price"
                       type="number" v-model="editing.price"/>


            <mdb-input id="chooseOnJoin" label="User May Select" type="checkbox"
                       v-model="editing.chooseOnJoin"/>
            <mdb-input id="isFreeTrialPlan" label="Free Trial Plan?" type="checkbox"
                       v-model="editing.isFreeTrialPlan"/>
            <div class="text-info small" v-if="editing.isFreeTrialPlan && editing.chooseOnJoin"><i>Users can only select this free trial plan
              if they haven't already completed a free trial. </i></div>
            <mdb-input v-if="editing.isFreeTrialPlan" id="freeTrialDays" label="#Days in Free Trial" type="number" :step="1"
                       v-model="editing.freeTrialDays"/>
            <mdb-input id="isMembershipPlan" label="Includes non-free memberships?" type="checkbox"
                       v-model="editing.isMembershipPlan"/>

            <mdb-input id="isOwnershipPlan" label="Includes group ownership?" type="checkbox"
                       v-model="editing.isOwnershipPlan"/>

            <mdb-input id="allowSwitch" label="Allow switching to another plan?" type="checkbox"
                       v-model="editing.allowSwitch"/>

            <gp-select :dataObj="editing" :possible-values="tiers" :required="false"
                       v-show="editing.isMembershipPlan"
                       label="Paid Membership Max Tier" propertyName="includedGroupTier"/>

            <mdb-input v-if="editing.isOwnershipPlan" :step="1"
                       label="Max Groups Allowed" placeholder="# Groups Allowed"
                       type="number" v-model="editing.maxGroups"/>
            <mdb-input v-if="editing.isOwnershipPlan" :step="1"
                       label="Max Members Allowed Across Owned Groups" placeholder="# Members Allowed"
                       type="number" v-model="editing.maxMembers"/>


            <mdb-card v-if="editing.id">
              <mdb-card-header class="secondary-color">
                <h5>Country-Specific Pricing</h5>
              </mdb-card-header>
              <mdb-card-body>
                <mdb-tbl striped responsive>
                  <mdb-tbl-head>
                    <tr>
                      <th>Country</th>
                      <th>Price</th>
                      <th>&nbsp;</th>
                    </tr>
                  </mdb-tbl-head>
                  <mdb-tbl-body>
                    <tr v-for="c in editing.countries" :key="c.id">
                      <td>{{ c.name }}</td>
                      <td style="text-align:left;">{{ c.currencySymbol }} {{ c.price }} ( {{ c.currencyCode }} )</td>
                      <td>
                        <mdb-btn floating tag="a" size="sm" icon="pencil" @click="editCountry(c)"/>
                        <mdb-btn floating tag="a" size="sm" icon="trash" color="warning" @click="deleteCountry(c)"/>
                      </td>
                    </tr>
                  </mdb-tbl-body>
                </mdb-tbl>
                <mdb-btn floating tag="a" icon="plus-circle" @click="newCountry"/>
              </mdb-card-body>

            </mdb-card>

            <gp-select :dataObj="editing" :possible-values="selectablePlans" :required="false"
                       label="On Expiration, Convert To" propertyName="expirationPlan"/>

            <mdb-input label="Send Email Before Expiration - # Days" placeholder="# Days Before" type="number"
                       v-model="editing.renewalPreDays"/>

            <mdb-card>
              <mdb-card-header class="secondary-color">
                <h5>Message Text</h5>
              </mdb-card-header>
              <mdb-card-body>
                <mdb-tabs :active="activeTab" :links="tabLinks" card
                          class="mb-5"
                          tabs
                >
                  <div slot="Marketing">
                    <editor id="marketing-features" :init="editorConfig" v-model="editing.marketingFeatures"/>
                  </div>
                  <div slot="Subscription Starts">
                    <editor id="start-text"
                            :init="editorConfig"
                            v-model="editing.startText"/>
                  </div>
                  <div slot="Pre Expiration" v-if="editing.renewalPreDays && editing.renewalPreDays>0">
                    <editor id="renewal-text"
                            :init="editorConfig"
                            v-model="editing.renewalText"/>
                  </div>
                  <div slot="Expiration">
                    <editor id="expiration-text" :init="editorConfig" v-model="editing.expirationEmailText"/>
                  </div>

                </mdb-tabs>
              </mdb-card-body>
            </mdb-card>

            <mdb-btn @click="savePlan" color="success" icon="save">Save</mdb-btn>
            <mdb-btn @click="cancelEditing" color="danger" icon="times-circle">Cancel</mdb-btn>
          </mdb-card-body>
        </mdb-card>
      </div>

      <mdb-modal v-if="showCountryModal" @close="showCountryModal=false">
        <mdb-modal-header class="secondary-color" close>
          Country-Specific Price
        </mdb-modal-header>
        <mdb-modal-body>
          <gp-select :dataObj="editingCountry" :possible-values="selectableCountries" :required="true"
                     label="Country" propertyName="countryId"/>
          <mdb-input type="number" :step="0.01" v-model="editingCountry.price" label="Price"/>
          <mdb-btn :disabled="!editingCountry.countryId" @click="saveCountry" icon="save" color="success">Save</mdb-btn>
          <mdb-btn @click="showCountryModal=false" icon="minus-circle" color="warning">Cancel</mdb-btn>
        </mdb-modal-body>
      </mdb-modal>

      <yes-no-modal @yes="doDeleteCountry" @no="showDeleteModal=false" v-if="showDeleteModal"/>
      <yes-no-modal @yes="doDeletePlan" @no="showDeletePlanModal=false" v-if="showDeletePlanModal"/>

    </div>
  </mdb-container>

</template>
<script>
import api from '../../services/groupsPlaceApi.js'
import {mapState} from 'vuex'
import gpSelect from "../common/GPSelect";
import editor from '@tinymce/tinymce-vue'
import YesNoModal from "../common/YesNoModal";

import {
  mdbBtn,
  mdbCard,
  mdbCardBody,
  mdbCardHeader,
  mdbContainer,
  mdbIcon,
  mdbInput,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbTabs,
  mdbTbl,
  mdbTblBody,
  mdbTblHead
} from 'mdbvue';
import {Datetime} from 'vue-datetime'
import moment from 'moment'

export default {
  name: "system-subscription-plans",
  order: 1,
  components: {
    mdbContainer, mdbTbl, mdbTblHead, mdbTblBody, mdbIcon, mdbBtn, mdbInput,
    mdbCard, mdbCardBody, mdbCardHeader, mdbTabs,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    Datetime,
    gpSelect,
    editor,
    YesNoModal
  },
  computed: mapState({
                       userDetails: state => state.userDetails,
                       settings: state => state.settings
                     }),
  data() {
    return {
      plans: [],
      tiers: [],
      currentPlan: null,
      editing: null,
      editingCountry: null,
      editingCountryObj: null,
      showCountryModal: false,
      deleteCandidate: null,
      showDeleteModal: false,
      showDeletePlanModal: false,
      showMain: true,
      showEdit: false,
      selectablePlans: [],
      countries: [],
      selectableCountries: [],
      editorConfig: {
        selector: '.tinymce_group_content',
        browser_spellcheck: true,
        branding: false,
        plugins: ["image", "link", "code", "paste", "table", "lists"],
        toolbar: ["undo redo cut copy paste bold italic underline numlist bullist fontsizeselect | alignleft alignright aligncenter | styleselect | image link unlink code "],
        image_advtab: true,
        style_formats_merge: true,
        paste_as_text: true,
        paste_data_images: false,
        relative_urls: false,
        remove_script_host: false,
        height: 400
      },
      tabLinks: [{text: "Marketing"}, {text: "Subscription Starts"}, {text: "Pre Expiration"}, {text: "Expiration"}],
      activeTab: 0
    }
  },
  mounted() {
    document.title = "System Subscription Plans"
    this.$store.dispatch("setExecuting", true)
    api.get("/admin/plans").then(
      (results) => {
        this.plans = results.data
        this.selectablePlans.push({id: null, text: "---NONE---"})
        for (let i = 0; i < this.plans.length; i++) {
          let row = this.plans[i]
          let sp = {text: row.planName, value: row.id}
          this.selectablePlans.push(sp)
        }
      },
      (error) => {
        if (error.response.status == 403 || error.response.status == 401) {
          this.$router.push({path: '/403', query: {redirect: this.$route.fullPath}})
          return
        }
        this.$notify.error({message: error.response.data, timeOut: 20000})
      }).then(() => {
      this.$store.dispatch("setExecuting", false)
    })
    api.get("/public/countryList").then(
      (results) => {
        this.countries = results.data
        for (const country of this.countries) {
          this.selectableCountries.push({text: country.name + " (" + country.currencyCode + ")", value: country.id})
        }
        this.countries.sort((a, b) => (a.country > b.country ? 1 : -1))
      },
      (error) => {
        this.$notify.error({message: "Error fetching countries.", timeOut: 3000})
      }).then(() => {
    })
    api.get("/admin/planTiers").then(
      (results) => {
        this.tiers = results.data
      },
      (error) => {
        this.$notify.error({message: "Error fetching tiers.", timeOut: 3000})
      }).then(() => {
    })

  },
  methods: {
    newCountry() {
      this.editingCountry = {
        id: null,
        countryId: null,
        price: 0,
        additionalGroupPrice: 0,
        ownerPaysPrice: 0
      }
      this.showCountryModal = true
    },
    savePlan() {
      this.$store.dispatch("setExecuting", true)
      delete this.editing['subscriptions']
      api.post("/admin/plans", this.editing).then(
        (results) => {
          if (this.editing.id) Object.assign(this.currentPlan, results.data)
          else {
            (this.plans.push(results.data))
            this.editing.id = results.data.id
            this.currentPlan = results.data
          }
          this.$notify.success({message: "Success", timeOut: 2000})
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    deleteCountry(c) {
      this.deleteCandidate = c
      this.showDeleteModal = true
    },
    deletePlan(p) {
      this.deleteCandidate = p
      this.showDeletePlanModal = true
    },
    doDeletePlan() {
      this.showDeletePlanModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/admin/plans/" + this.deleteCandidate.id).then(
        () => {
          let loc = this.plans.indexOf(this.deleteCandidate)
          if (loc >= 0) this.plans.splice(loc, 1)
          this.deleteCandidate = null
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    doDeleteCountry() {
      this.showDeleteModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/admin/planCountry/" + this.deleteCandidate.id).then(
        () => {
          let loc = this.editing.countries.indexOf(this.deleteCandidate)
          this.editing.countries.splice(loc, 1)
          this.deleteCandidate = null
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    editCountry(c) {
      this.editingCountryObj = c
      this.editingCountry = JSON.parse(JSON.stringify(c))
      this.showCountryModal = true
    },
    saveCountry() {
      let countryExists = false
      if (!this.editing.countries) countryExists = false
      else {
        for (const c of this.editing.countries) {
          if (this.editingCountry.countryId == c.countryId && this.editingCountry.id != c.id) {
            countryExists = true
          }
        }
      }
      if (countryExists) {
        this.$notify.error({message: "Duplicate Country", timeOut: 5000})
        return
      }
      this.showCountryModal = false
      this.$store.dispatch("setExecuting", true)
      api.post("/admin/planCountry/" + this.editing.id, this.editingCountry).then(
        (results) => {
          if (this.editingCountry.id) {
            Object.assign(this.editingCountryObj, results.data)
            this.editingCountry = null
            this.editingCountryOjb = null
          } else (this.editing.countries.push(results.data))
          this.editingCountry = null
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
          this.showCountryModal = true
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    addPlan() {
      this.editing = {
        id: null,
        price: 0,
        term: "Yearly",
        planName: "",
        chooseOnJoin: false,
        satisfiesMembership: true,
        expirationPlan: null,
        renewalPreDays: 0,
        additionalGroupPrice: 0,
        ownerPaysPrice: 0,
        isFreeTrialPlan: false,
        isMembershipPlan: false,
        isOwnershipPlan: false,
        allowSwitch: false
      }
      this.showMain = false
      this.showEdit = true
    },
    clonePlan(plan) {
      window.scrollTo(0, 0)
      this.$store.dispatch("setExecuting", true)
      api.get("/admin/clonePlan/" + plan.id).then(
        (results) => {
          this.plans.unshift(results.data)
          this.currentPlan = this.plans[0]
          this.editing = JSON.parse(JSON.stringify(this.plans[0]));
          this.editing.testDate = moment().format()
          this.editing.countries.sort((a, b) => (a.name > b.name ? 1 : -1))
          this.showMain = false
          this.showEdit = true
          this.$notify.success({message: "Success", timeOut: 2000})
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    editPlan(plan) {
      window.scrollTo(0, 0)
      this.currentPlan = plan
      this.editing = JSON.parse(JSON.stringify(plan));
      this.editing.testDate = moment().format()
      this.editing.countries.sort((a, b) => (a.name > b.name ? 1 : -1))
      this.showMain = false
      this.showEdit = true
    },
    cancelEditing() {
      this.currentPlan = null
      this.editing = null
      this.showMain = true
      this.showEdit = false
    }

  }
};

</script>

